import { useCallback, useMemo } from 'react';
import { Linking } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { businessDetailQueryOptions, businessPhotosQueryOptions } from '@/api/queries/businesses';
import { menuItemsListQueryOptions } from '@/api/queries/menu-items';
import { openMapsHandoff } from '@/features/business/maps-handoff';
import { toBusinessViewerPhotos } from '../business-viewer-photos';

const PHOTO_STRIP_LIMIT = 6;

export function useBusinessDetail(id: string) {
  const { data: business, isLoading, error, refetch } = useQuery(
    businessDetailQueryOptions(id)
  );

  const { data: photosData } = useQuery(
    businessPhotosQueryOptions(id)
  );
  const photos = photosData?.items ?? [];
  const totalPhotoCount = photosData?.total ?? 0;

  const { data: menuItemsData } = useQuery(
    menuItemsListQueryOptions({ business_id: id, per_page: 5, sort_by: 'name' })
  );
  const menuItems = menuItemsData?.items ?? [];
  const menuItemsTotal = menuItemsData?.total ?? 0;

  // Captioned with each photo's check-in so the lightbox can show and link it.
  const displayPhotos = useMemo(
    () => toBusinessViewerPhotos(photos.slice(0, PHOTO_STRIP_LIMIT)),
    [photos]
  );

  const handleWebsitePress = useCallback(() => {
    if (business?.website) {
      Linking.openURL(business.website);
    }
  }, [business?.website]);

  const handlePhonePress = useCallback(() => {
    if (business?.phone) {
      Linking.openURL(`tel:${business.phone}`);
    }
  }, [business?.phone]);

  const handleEmailPress = useCallback(() => {
    if (business?.email) {
      Linking.openURL(`mailto:${business.email}`);
    }
  }, [business?.email]);

  const handleMapsPress = useCallback(() => {
    if (business) {
      void openMapsHandoff(business);
    }
  }, [business]);

  return {
    business: business ?? null,
    isLoading,
    error,
    refetch,
    displayPhotos,
    totalPhotoCount,
    menuItems,
    menuItemsTotal,
    handleWebsitePress,
    handlePhonePress,
    handleEmailPress,
    handleMapsPress,
  };
}
